import { useEffect, useRef, type ComponentProps } from "react";
import {
  Root,
  Trigger,
  Portal,
  Close,
  Overlay,
  Content,
  Title,
  Description,
} from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { cn } from "@/lib/utils";

type DialogHistoryState = { __dialogDepth?: number } | null;

// Uses React Router's `navigate`, not raw window.history — that desyncs the router's own history index and can jump back further than one entry.
function useCloseOnBackNavigation(
  open: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined
) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const pushedRef = useRef(false);
  const closedByPopRef = useRef(false);
  const depthRef = useRef(0);
  // Only close on a depth-decrease once our own marker is confirmed landed, else a fresh mount can see a stale pre-existing POP and self-close.
  const markerConfirmedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    // Deferred so a StrictMode double-invoke cancels the phantom first pass before it touches history.
    let cancelled = false;
    const parentDepth = (location.state as DialogHistoryState)?.__dialogDepth ?? 0;
    const myDepth = parentDepth + 1;
    queueMicrotask(() => {
      if (cancelled) return;
      depthRef.current = myDepth;
      markerConfirmedRef.current = false;
      navigate(location.pathname + location.search + location.hash, {
        state: { ...(location.state as object | null), __dialogDepth: myDepth },
      });
      pushedRef.current = true;
    });
    return () => {
      cancelled = true;
      if (pushedRef.current) {
        pushedRef.current = false;
        if (!closedByPopRef.current) {
          navigate(-1);
        }
        closedByPopRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!pushedRef.current) return;
    const currentDepth = (location.state as DialogHistoryState)?.__dialogDepth ?? 0;
    if (currentDepth === depthRef.current) {
      markerConfirmedRef.current = true;
      return;
    }
    if (markerConfirmedRef.current && navigationType === "POP" && currentDepth < depthRef.current) {
      closedByPopRef.current = true;
      pushedRef.current = false;
      onOpenChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigationType]);
}

function Dialog({ open, onOpenChange, ...props }: ComponentProps<typeof Root>) {
  useCloseOnBackNavigation(open, onOpenChange);
  return <Root data-slot="dialog" open={open} onOpenChange={onOpenChange} {...props} />;
}

function DialogTrigger({ ...props }: ComponentProps<typeof Trigger>) {
  return <Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: ComponentProps<typeof Portal>) {
  return <Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: ComponentProps<typeof Close>) {
  return <Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: ComponentProps<typeof Overlay>) {
  return (
    <Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto",
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: ComponentProps<typeof Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay>
        <Content
          data-slot="dialog-content"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative z-50 grid w-full max-w-[calc(100%-2rem)] max-h-[calc(100vh-6rem)] overflow-y-auto gap-4 rounded-lg border p-6 shadow-lg duration-200 my-8 sm:max-w-lg",
            className
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <Close
              data-slot="dialog-close"
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </Close>
          )}
        </Content>
      </DialogOverlay>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: ComponentProps<typeof Title>) {
  return (
    <Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: ComponentProps<typeof Description>) {
  return (
    <Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
